import { ViewerStanding } from './ViewerStanding';
import { computed } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import type { FirmWithParentsAndChildren } from '@thinkalpha/ui-types/types/admin/firms';
import { observeOutcome, settle } from 'src/admin/lib/queryOutcome';
import type { Outcome, RefusalStatus } from 'src/admin/lib/queryOutcome';
import { getUserFirmsQuery } from 'src/queries/firms';
import { getUserSecurityPackageQuery } from 'src/queries/user';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

type SecurityPackageData = z.infer<typeof SecurityPackage>;

const unreadable = (httpStatus: RefusalStatus): Outcome<never> => ({
    status: 'failed',
    error: new Error(`The viewer's access could not be read (HTTP ${httpStatus}).`),
});

export interface AdminViewerScopeDeps {
    queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    activeUserModel: ReactBindings['ActiveUserModel'];
}

/**
 * The viewer's perspective for the admin pages: the real superuser toggle, the security package
 * (`GET /users/me/security-package`), and the firms the viewer belongs to (`GET /firms?userId=`).
 * Shared by the models that assemble page sources so every page describes the viewer the same way.
 */
export class AdminViewerScope implements Disposable {
    readonly #stack = new DisposableStack();

    /** Superuser privileges are on: the perspective every request currently carries. */
    readonly isSuperuser: ReadonlySignal<boolean>;
    /** The account could turn privileges on. */
    readonly canElevate: ReadonlySignal<boolean>;
    readonly standing: ReadonlySignal<Outcome<ViewerStanding>>;

    constructor({ queryObserverModelFactory, activeUserModel }: AdminViewerScopeDeps) {
        this.isSuperuser = computed(
            () => activeUserModel.isSuperuser.value && activeUserModel.isSuperUserModeEnabled.value,
        );
        this.canElevate = computed(
            () => activeUserModel.isSuperuser.value && !activeUserModel.isSuperUserModeEnabled.value,
        );

        const securityPackage = observeOutcome<SecurityPackageData>(
            this.#stack,
            queryObserverModelFactory,
            computed(() => getUserSecurityPackageQuery('me')),
        );
        const memberFirms = observeOutcome<FirmWithParentsAndChildren[]>(
            this.#stack,
            queryObserverModelFactory,
            computed(() => getUserFirmsQuery(activeUserModel.userId.value)),
        );

        this.standing = computed<Outcome<ViewerStanding>>(() => {
            const settlement = settle({ securityPackage: securityPackage.value, memberFirms: memberFirms.value });
            if (settlement.status !== 'settled') return settlement;
            const { securityPackage: pkg, memberFirms: firms } = settlement.outcomes;
            if (pkg.status === 'refused') return unreadable(pkg.httpStatus);
            if (firms.status === 'refused') return unreadable(firms.httpStatus);
            return {
                status: 'ok',
                data: new ViewerStanding({
                    isSuperuser: this.isSuperuser.value,
                    canElevate: this.canElevate.value,
                    accessList: pkg.data.accessList,
                    memberFirms: firms.data,
                }),
            };
        });
    }

    [Symbol.dispose](): void {
        this.#stack.dispose();
    }
}
